import Guid from "devextreme/core/guid";
import { parseNumber } from "devextreme/localization"
/**
 * Class to represent Logic which can parse Dev-Extreme Data-Grid Filters to a valid ODataV4 format.
 */
class TableControlOData4Parser {

    //#region  Constructors

    /**
     * Constructor.
     */
    constructor() {
        this.OData4Formatters = this.BuildODataFormatters();
    }

    //#endregion

    //#region Properties

    /**
     * Gets or sets the available ODataV4 Formatters.
     */
    protected OData4Formatters: any;

    //#endregion

    //#region  Public Methods

    /**
     * Creates the OData Filter query from the given Filter.
     * @param filter The filter which should be parsed.
     * @param modelMetadata Optional ModelMetadata to identify to which type some filters needs to be parsed.
     * @returns The generated ODataFilter-String.
     */
    public CreateODataFilterQuery(filter: any, modelMetadata: string) {
        if (filter != null) {
            let metadata = null;
            if (modelMetadata?.length > 0) {
                metadata = JSON.parse(modelMetadata);
            }

            return this.CompileCoreCriteria(filter, metadata);
        }

        return null;
    }

    //#endregion

    //#region  Protected Methods

    /**
     * Compiles a group of criterias and joins them together into a single filter-string.
     */
    protected CompileCriteriaGroup(criteria, metadata) {
        let bag = [];
        let groupOperator: string;
        let nextGroupOperator: string;

        let self = this;
        
        $.each(criteria, (function(index, criterion) {
            if (Array.isArray(criterion)) {
                bag.push("(".concat(self.CompileCoreCriteria(criterion, metadata), ")"));
                groupOperator = nextGroupOperator;
                nextGroupOperator = "and";
            } else {
                nextGroupOperator = self.IsConjunctiveOperator(this) ? "and" : "or";
            }
        }));
        return bag.join(" ".concat(groupOperator, " "));
    }

    /**
     * Compiles multiple criterias into a single filter-string.
     */
    protected CompileCoreCriteria = (criteria, metadata) => {
        if (Array.isArray(criteria[0])) {
            return this.CompileCriteriaGroup(criteria, metadata);
        }
        if (this.IsUnaryOperation(criteria)) {
            return (innerCriteria => {
                let op = innerCriteria[0];
                let crit = this.CompileCoreCriteria(innerCriteria[1], metadata);
                if ("!" === op) {
                    return "not (".concat(crit, ")");
                }
            })(criteria);
        }
        return this.CompileCriteria(criteria, metadata);
    };

    /**
     * Compiles a single criteria into a filter-string.
     */
    protected CompileCriteria(criteria, metadata) {
        criteria = this.NormalizeBinaryCriterion(criteria);
        let fieldName = criteria[0];
        let op = criteria[1];
        let formatter = this.OData4Formatters[op.toLowerCase()];
        let value = criteria[2];
        return formatter(fieldName.replace(/\./g, "/"), this.SerializeValueV4(fieldName, value, metadata));
    }

    /**
     * Creates the available OData Formatters.
     */
    protected BuildODataFormatters() {
        return {
            "=": this.CreateBinaryOperationFormatter("eq"),
            "<>": this.CreateBinaryOperationFormatter("ne"),
            ">": this.CreateBinaryOperationFormatter("gt"),
            ">=": this.CreateBinaryOperationFormatter("ge"),
            "<": this.CreateBinaryOperationFormatter("lt"),
            "<=": this.CreateBinaryOperationFormatter("le"),
            startswith: this.CreateStringFuncFormatter("startswith"),
            endswith: this.CreateStringFuncFormatter("endswith"),
            contains: this.CreateStringFuncFormatter("contains"),
            notcontains: this.CreateStringFuncFormatter("not contains")
        };
    }

    /**
     * Creates the delegate to format Binary Operations.
     */
    protected CreateBinaryOperationFormatter(op) {
        return (prop, val) => "".concat(prop, " ").concat(op, " ").concat(val);
    } 

    /**
     * Creates the delegate for format String Operations.
     */
    protected CreateStringFuncFormatter(op) {
        return (prop, val) => {
            var bag = [op, "("];

            prop = -1 === prop.indexOf("tolower(") ? "tolower(".concat(prop, ")") : prop;
            val = val.toLowerCase();
            bag.push(prop, ",", val);
            bag.push(")");
            return bag.join("");
        };
    }

    /**
     * Checks if a criteria is negated.
     */
    protected IsUnaryOperation(crit: any[]) {
        return "!" === crit[0] && Array.isArray(crit[1]);
    }

    /**
     * Normalizes a Binary Operation.
     */
    protected NormalizeBinaryCriterion(crit: string | any[]) {
        return [crit[0], crit.length < 3 ? "=" : String(crit[1]).toLowerCase(), crit.length < 2 ? true : crit[crit.length - 1]];
    }

    /**
     * Serializes a OData-Value to be compatible with ODataV4.
     */
    protected SerializeValueV4(name, value: any, metadata) {
        let type = this.GetEdmType(name, metadata);

        if (value === "null" || value === null) {
            return value;
        }

        if (type != null) {
            return this.FormatPrimitiveType(value, type);
        }

        if (value instanceof Date) {
            return this.FormatISO8601(value, false, false);
        }

        let guidValue = this.tryParseGuid(value);
        if (value instanceof Guid || guidValue !== null) {
            return guidValue ? guidValue.valueOf() : value.valueOf();
        }

        if (type == 'Edm.Guid') {
            return value;
        }

        if (Array.isArray(value)) {
            return "[".concat(value.map(item => this.SerializeValueV4(name, item, metadata)).join(","), "]");
        }

        return this.SerializeValueV2(value);
    }

    /**
     * Serializes a OData-Value to be compatible with ODataV2.
     */
    protected SerializeValueV2(value) {
        if (value instanceof Date) {
            return this.SerializeDate(value);
        }
        if (value instanceof Guid) {
            return "guid'".concat(value.toString(), "'");
        }
        if ("string" === typeof value) {
            return this.SerializeString(value);
        }
        return String(value);
    }

    /**
    * Validate via RegEx if the input string is a Guid.
    */
    protected isGuid(value) {
        const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
        return guidRegex.test(value);
    }

    /**
    * Try to parse a string into a Guid.
    */
    protected tryParseGuid(value) {
        if (this.isGuid(value)) {
            return value;
        } else {
            return null;
        }
    }

    /**
     * Formats a Primitive-Value into its format.
     */
    protected FormatPrimitiveType(value: any[], type: string) {
        switch (type) {
            case "Edm.Int16":
            case "Edm.Int32":
            case "Edm.Int64":
                return value.toString();
            case "Edm.Guid":
                return value;
            case "Edm.String":
                return this.SerializeString(value);
            case "Edm.Boolean":
                return value == null ? false : value.toString().toLowerCase();
            case "Edm.DateTime":
                return this.SerializeDate(value);
            case "Edm.DateTimeOffset":
                return this.SerializeDateTimeOffset(value);
            case "Edm.Decimal":
                let parsedFloat = parseNumber((value as any), 'decimal');
                if (parsedFloat != null && !isNaN(parsedFloat)) {
                    return parsedFloat.toLocaleString("en-US");
                }
                return value.toString();
            default:
                return null;
        }
    }

    /**
     * Extracts the maching Edm-Type from the given metadata-object.
     */
    protected GetEdmType(name, metadata) {
        let result = null;

        if (metadata != null &&
            typeof metadata === 'object') {

            let typeDef = metadata[name];
            if (typeDef != null && typeDef.$Type !== undefined) {
                return typeDef.$Type;
            }

            for (const property in metadata) {
                let propResult = this.GetEdmType(name, metadata[property]);
                if (propResult != null) {
                    return propResult;
                }
            }
        }

        return result;
    }

    /**
     * Serializes a Date to be used with OData.
     */
    protected SerializeDate(date) {
        let d = this.parseISOString(date);
        return "datetime'".concat(this.FormatISO8601(d, true, false), "'");
    } 

    /**
    * Serializes a DateTimeOffset to be used with OData.
    */
    protected SerializeDateTimeOffset(date) {
        if (date === 'null') {
            return null;
        }

        let d = this.parseISOString(date);
        return this.FormatISO8601(d, true, false);
    }

    /**
     * Parses an ISO string into a Date Object
    */
    protected parseISOString(s) {
        let b = s.toString().split(/\D+/);
        return new Date(b[2], --b[1], b[0], b[3], b[4], b[5]);
    }

    /**
     * Serializes a String to be used with OData.
     */
    protected SerializeString(value) {

        // escapes all special characters which could brick the request in the backend
        let queryString = value.replace(/'/g, "''");
        queryString = queryString.replace(/\+/g, "%2B");
        queryString = queryString.replace(/\?/g, "%3F");
        queryString = queryString.replace(/#/g, "%23");
        queryString = queryString.replace(/&/g, "%26");

        return "'".concat(queryString, "'");
    }

    /**
     * Formats the Date to be compatible with the ISO8601-Format.
     */
    protected FormatISO8601(date, skipZeroTime, skipTimezone) {

        // hack to properly parse the date
        date.setMinutes(date.getMinutes() + date.getTimezoneOffset());

        let bag = [];
        let padLeft2 = text => this.Pad(text, 2, false);
        bag.push(date.getFullYear());
        bag.push("-");
        bag.push(padLeft2(date.getMonth() + 1));
        bag.push("-");
        bag.push(padLeft2(date.getDate()));
        if (!(skipZeroTime && date.getHours() + date.getMinutes() + date.getSeconds() + date.getMilliseconds() < 1)) {
            bag.push("T");
            bag.push(padLeft2(date.getHours()));
            bag.push(":");
            bag.push(padLeft2(date.getMinutes()));
            bag.push(":");
            bag.push(padLeft2(date.getSeconds()));
            if (date.getMilliseconds()) {
                bag.push(".");
                bag.push(this.Pad(date.getMilliseconds(), 3, false));
            }
            if (!skipTimezone) {
                bag.push("Z");
            }
        }
        return bag.join("");
    }

    /**
     * Fills up text with leading or tailing "0" till the given length is reached.
     */
    protected Pad(text, length, right) {
        text = String(text);
        while (text.length < length) {
            text = right ? "".concat(text, "0") : "0".concat(text);
        }
        return text;
    }

    /**
     * Checks if the condition is Conjunctive (AND) or not (OR).
     */
    protected IsConjunctiveOperator(condition) {
        return /^(and|&&|&)$/i.test(condition);
    }

    //#endregion
}
export default TableControlOData4Parser;
